import React, { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { Col, Row } from 'reactstrap';
import { v4 as uuid } from 'uuid';
import { eachDayOfInterval, isSameDay } from 'date-fns';
import { change } from 'redux-form';
import {
  changeLoadingTrue,
  changeLoadingFalse,
} from '../../../redux/actions/loadingActions';
import SmartCard from '../../../shared/components/SmartCard';
import FormAcademicCalendar from './Form';
import GraphQL from '../../../GraphQL';
import { showNotificationAutoDestroy } from '../../../shared/components/ShowNotification';
import {
  academicCalendarListByFilter,
  listDaysByAcademicCalendar,
  saveAcademicCalendarMutation,
  workingDays,
} from '../../../shared/queries/AcademicCalendar';
import loadAccessListByUser from '../../../shared/queries/User';
import YearCalendar from './YearCalendar';
import Weekdays from './WeekDays';
import FullScreenDialog from '../../../shared/components/FullScreenDialog';
import FullScreenForm from './FullScreenForm';
import { TYPE_MODAL } from '../../../shared/utils/constants';
import { schoolTermListAllQuery } from '../../../shared/queries/SchoolTerm';

const AcademicCalendarFormHandler = (props) => {
  const { match, t, history, dispatch } = props;
  const [dto, setDto] = useState({
    id: 0,
    name: '',
    stepQuantity: 1,
    calendarCycles: [],
    calendarMarkers: [],
    active: true,
  });
  const [formOn, setFormOn] = useState(false);
  const [weekdays, setWeekDays] = useState([]);
  const [calendarDays, setCalendarDays] = useState([]);
  const [calendarMarkers, setCalendarMarkers] = useState([]);
  const [calendarCycles, setCalendarCycles] = useState([]);
  const [openScreen, setOpenScreen] = useState(false);
  const [contentForm, setContentForm] = useState({});
  const [listOfTerms, setListOfTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState({});
  const [totalSchoolDay, setTotalSchoolDay] = useState(0);

  const backClickHandler = () => {
    history.push('/minime/calendario-academico');
  };

  const showLoading = () => {
    dispatch(changeLoadingTrue());
  };

  const hideLoading = () => {
    dispatch(changeLoadingFalse());
  };

  const toggleFullScreen = () => {
    setOpenScreen(!openScreen);
  };

  const showDialog = (titleFullScreen, values) => {
    setContentForm({ titleFullScreen, ...values });
    toggleFullScreen();
  };

  const selectCycle = (values) => {
    const updatedValues = { ...values, type: TYPE_MODAL.CYCLE };
    showDialog(updatedValues.name, updatedValues);
  };

  const selectCalendar = (values) => {
    let updatedValues = { type: TYPE_MODAL.MARKING };
    if (values.name) {
      updatedValues = { ...updatedValues, ...values };
    } else {
      updatedValues = { ...updatedValues, schoolDay: true };
    }
    showDialog(t('general.add-marking'), updatedValues);
  };

  const selectEvent = (values) => {
    const updatedValues = {
      ...values,
      beginInMilli: values.dateInMilli,
      endInMilli: values.dateInMilli,
    };
    showDialog(t('general.event'), updatedValues);
  };

  const onChangeDto = (field, values) => {
    const updatedDto = { ...dto };
    updatedDto[field] = values;
    setDto(updatedDto);
  };

  const convertStringToArrayBoolean = (value) => {
    const arrChar = value.split(';');
    arrChar.pop();
    return arrChar.map((item) => Boolean(Number(item)));
  };

  const weekDaysList = () => {
    showLoading();
    const query = workingDays();
    return GraphQL(query, {})
      .then((res) => {
        setWeekDays(convertStringToArrayBoolean(res.findSchool.workingDays));
        return res.findSchool.workingDays;
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const totalSchoolDays = (daysOfCalendar) => {
    let total = 0;
    daysOfCalendar.forEach((day) => {
      const weekDay = new Date(day.dateInMilli).getUTCDay();
      if (day.markers && day.markers.length > 0) {
        let isSchoolDayByMarker = true;
        let isSchoolDayByDate = false;
        const isSchoolDayByWeekDay = weekdays[weekDay];
        day.markers.forEach((marker) => {
          if (marker.cycle && marker.cycle.uuid) {
            isSchoolDayByDate =
              day.dateInMilli >= marker.cycle.beginInMilli &&
              day.dateInMilli <= marker.cycle.endInMilli;
          }

          if (marker.marker) {
            if (isSchoolDayByMarker) {
              isSchoolDayByMarker = marker.marker.schoolDay;
            }
          }
        });

        if (
          day.schoolDay &&
          isSchoolDayByMarker &&
          isSchoolDayByDate &&
          isSchoolDayByWeekDay
        ) {
          total += 1;
        }
      }
    });
    setTotalSchoolDay(total);
  };

  const changeSchoolDayInCalendarDays = (calendar) => {
    const updatedCalendar = calendar.map((day) => {
      const weekDay = new Date(day.dateInMilli).getUTCDay();
      if (day.markers && day.markers.length > 0) {
        let isSchoolDayByMarker = true;
        let isSchoolDayByDate = false;
        const isSchoolDayByWeekDay = weekdays[weekDay];
        day.markers.forEach((marker) => {
          if (marker.cycle && marker.cycle.uuid) {
            isSchoolDayByDate =
              day.dateInMilli >= marker.cycle.beginInMilli &&
              day.dateInMilli <= marker.cycle.endInMilli;
          }

          if (marker.marker) {
            if (isSchoolDayByMarker) {
              isSchoolDayByMarker = marker.marker.schoolDay;
            }
          }
        });

        return {
          ...day,
          schoolDay:
            isSchoolDayByMarker && isSchoolDayByDate && isSchoolDayByWeekDay,
        };
      }
      return { ...day };
    });
    setCalendarDays(updatedCalendar);
  };

  const loadSchoolTermList = () => {
    showLoading();
    const query = schoolTermListAllQuery();
    GraphQL(query, {})
      .then((res) => {
        hideLoading();
        setListOfTerms(res.schoolTermListAll);
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const loadDaysByAcademicCalendar = (weekdayStr, schoolTerm) => {
    const query = listDaysByAcademicCalendar(dto.id, schoolTerm, weekdayStr);
    GraphQL(query, {})
      .then((res) => {
        hideLoading();
        setCalendarDays(res.academicCalendarListDaysByAcademicCalendar);
        setDto({
          ...dto,
          calendarDays: res.academicCalendarListDaysByAcademicCalendar,
          weekdays: convertStringToArrayBoolean(weekdayStr),
          schoolTerm: { id: schoolTerm },
        });
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const schoolTermByLoggedUser = () => {
    const query = loadAccessListByUser();
    return GraphQL(query, {})
      .then((res) => {
        return res.accessListByUser.loggedUser.selectedTermId;
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const loadFiltersValues = async () => {
    const weekdayStr = await weekDaysList();
    const schoolTermIdByLoggedUser = await schoolTermByLoggedUser();
    return { weekdayStr, schoolTermIdByLoggedUser };
  };

  const findById = (id) => {
    showLoading();
    const query = academicCalendarListByFilter({ id });
    GraphQL(query, {})
      .then((res) => {
        hideLoading();
        if (res.academicCalendarListByFilter) {
          const workingdays = [];
          workingdays[0] = res.academicCalendarListByFilter[0].sunday;
          workingdays[1] = res.academicCalendarListByFilter[0].monday;
          workingdays[2] = res.academicCalendarListByFilter[0].tuesday;
          workingdays[3] = res.academicCalendarListByFilter[0].wednesday;
          workingdays[4] = res.academicCalendarListByFilter[0].thursday;
          workingdays[5] = res.academicCalendarListByFilter[0].friday;
          workingdays[6] = res.academicCalendarListByFilter[0].saturday;
          setDto({
            ...res.academicCalendarListByFilter[0],
          });
          setWeekDays(workingdays);
          setCalendarMarkers(
            res.academicCalendarListByFilter[0].calendarMarkers
          );
          setCalendarCycles(res.academicCalendarListByFilter[0].calendarCycles);
          setCalendarDays(res.academicCalendarListByFilter[0].calendarDays);
        } else {
          showNotificationAutoDestroy(
            'danger',
            t('notifications.error'),
            t('notifications.record-not-found')
          );
          backClickHandler();
        }
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const handleSubmit = (values) => {
    showLoading();
    const mutation = saveAcademicCalendarMutation(
      values,
      calendarDays,
      calendarMarkers
    );
    GraphQL(mutation, {})
      .then(() => {
        hideLoading();
        backClickHandler();
        showNotificationAutoDestroy(
          'success',
          t('success.title'),
          t('success.successfully-saved')
        );
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const addValuesCalendarMarkers = (formModal) => {
    const calendarMarker = {
      id: formModal.id || 0,
      name: formModal.name,
      color: formModal.color || '#FFCDD2',
      schoolDay: formModal.schoolDay,
      uuid: formModal.uuid || uuid(),
    };
    let updatedCalendarMarkers = [...calendarMarkers];
    const index = updatedCalendarMarkers.findIndex(
      (marker) => marker.uuid === calendarMarker.uuid
    );
    if (index !== -1) {
      updatedCalendarMarkers[index] = calendarMarker;
    } else {
      updatedCalendarMarkers = [...updatedCalendarMarkers, calendarMarker];
    }
    let updatedCalendar = [...calendarDays];
    updatedCalendar = updatedCalendar.map((day) => {
      if (day.markers.length) {
        const markers = day.markers.map((item) => {
          if (item.marker && item.marker.uuid === calendarMarker.uuid) {
            return { ...item, marker: calendarMarker };
          }
          return item;
        });
        return { ...day, markers };
      }
      return day;
    });
    setCalendarDays(updatedCalendar);
    setCalendarMarkers(updatedCalendarMarkers);
  };

  const removeAllCyclesInCalendar = (
    updatedCalendarDaysMarkers,
    calendarCycle
  ) => {
    updatedCalendarDaysMarkers.forEach((calendarDay) => {
      const markerIndex = calendarDay.markers.findIndex(
        (marker) => marker.cycle && marker.cycle.uuid === calendarCycle.uuid
      );
      if (markerIndex > -1) {
        calendarDay.markers.splice(markerIndex, 1);
      }
    });
  };

  const addCycleInCalendar = (
    date,
    updatedCalendarDaysMarkers,
    calendarCycle,
    color
  ) => {
    return updatedCalendarDaysMarkers.map((day) => {
      if (isSameDay(new Date(day.dateInMilli), new Date(date))) {
        if (
          day.markers &&
          day.markers.length &&
          day.markers.some(
            (marker) =>
              marker.cycle && marker.cycle.sequence === calendarCycle.sequence
          )
        ) {
          return day;
        }
        return {
          ...day,
          markers: [
            ...day.markers,
            { id: 0, cycle: { ...calendarCycle, color } },
          ],
          uuid: uuid(),
        };
      }
      return day;
    });
  };

  const addValuesCalendarCycles = (formModal) => {
    const calendarCycle = {
      id: formModal.id,
      name: formModal.name,
      sequence: formModal.sequence,
      beginInMilli: formModal.beginInMilli,
      endInMilli: formModal.endInMilli,
      uuid: formModal.uuid || uuid(),
      color: '#767CD4',
    };

    let updatedCalendarCycles = [...calendarCycles];
    const index = updatedCalendarCycles.findIndex(
      (marker) => marker.uuid === calendarCycle.uuid
    );
    if (index !== -1) {
      updatedCalendarCycles[index] = { ...calendarCycle };
    } else {
      updatedCalendarCycles = [...updatedCalendarCycles, calendarCycle];
    }
    const intervalDates = eachDayOfInterval({
      start: new Date(calendarCycle.beginInMilli),
      end: new Date(calendarCycle.endInMilli),
    });
    let updatedCalendarDaysMarkers = [...calendarDays];

    removeAllCyclesInCalendar(updatedCalendarDaysMarkers, calendarCycle);
    intervalDates.forEach((date, dateIndex) => {
      updatedCalendarDaysMarkers = addCycleInCalendar(
        date,
        updatedCalendarDaysMarkers,
        calendarCycle,
        dateIndex === 0 || dateIndex === intervalDates.length - 1
          ? '#767CD4'
          : ''
      );
    });
    setCalendarCycles(updatedCalendarCycles);
    setCalendarDays(updatedCalendarDaysMarkers);
    dispatch(
      change('AcademicCalendarForm', 'calendarCycles', updatedCalendarCycles)
    );
  };

  const addValuesEvents = (formModal) => {
    const { value, label, ...markerRest } = formModal.marker;
    const event = {
      id: formModal.newMarker ? 0 : formModal.id,
      title: formModal.title,
      description: formModal.description || '',
      marker: markerRest,
    };
    const intervalDates = eachDayOfInterval({
      start: new Date(formModal.beginInMilli),
      end: new Date(formModal.endInMilli),
    });

    let updatedCalendarDaysMarkers = [...calendarDays];
    intervalDates.forEach((date) => {
      updatedCalendarDaysMarkers = updatedCalendarDaysMarkers.map((day) => {
        if (isSameDay(new Date(day.dateInMilli), new Date(date))) {
          if (
            day.markers &&
            day.markers.length &&
            day.markers.some(
              (marker) =>
                marker.marker &&
                marker.marker.uuid === event.marker.uuid &&
                !formModal.newMarker
            )
          ) {
            day.markers.splice(
              day.markers.findIndex(
                (item) => item.marker && item.marker.uuid === event.marker.uuid
              ),
              1
            );
            return { ...day, markers: [...day.markers, { ...event }] };
          }
          return {
            ...day,
            markers: [...day.markers, { id: 0, ...event }],
            uuid: uuid(),
          };
        }
        return day;
      });
    });
    changeSchoolDayInCalendarDays(updatedCalendarDaysMarkers);
  };

  const addValuesInForm = (formModal) => {
    if (formModal.type === TYPE_MODAL.MARKING) {
      addValuesCalendarMarkers(formModal);
    } else if (formModal.type === TYPE_MODAL.CYCLE) {
      addValuesCalendarCycles(formModal);
    } else {
      addValuesEvents(formModal);
    }
    toggleFullScreen();
  };

  const onChangeWorkingDaysInCalendar = (values) => {
    const updatedCalendar = calendarDays.map((day) => {
      const weekDay = new Date(day.dateInMilli).getUTCDay();
      return {
        ...day,
        schoolDay: values[weekDay],
        workDay: values[weekDay],
      };
    });
    setWeekDays([...values]);
    setCalendarDays(updatedCalendar);
  };

  const removeEvent = (value) => {
    const updatedCalendarDays = [...calendarDays];
    updatedCalendarDays.forEach((day) => {
      if (day.dateInMilli === value.dateInMilli) {
        day.markers.splice(
          day.markers.findIndex(
            (item) => item.marker && item.marker.uuid === value.marker.uuid
          ),
          1
        );
      }
    });
    changeSchoolDayInCalendarDays(updatedCalendarDays);
  };

  const loadCalendarBySchoolTermId = (value) => {
    if (dto.id === 0 && value && selectedTerm && value.id !== selectedTerm.id) {
      setSelectedTerm(value);
      const weekdayStr = weekdays
        .map((item) => Number(item))
        .toString()
        .replace(/,/g, ';')
        .concat(';');
      showLoading();
      loadDaysByAcademicCalendar(weekdayStr, value.id);
    }
  };

  useEffect(() => {
    totalSchoolDays(calendarDays);
  }, [calendarDays]);

  useEffect(() => {
    if (match.params.id && match.params.id > 0) {
      if (dto.id !== parseInt(match.params.id, 0)) {
        findById(parseInt(match.params.id, 0));
      } else {
        setFormOn(true);
      }
    } else {
      setFormOn(true);
    }
  }, [dto]);

  useEffect(() => {
    if (parseInt(match.params.id, 0) === 0) {
      loadFiltersValues().then((filter) =>
        loadDaysByAcademicCalendar(
          filter.weekdayStr,
          filter.schoolTermIdByLoggedUser
        )
      );
    }
    loadSchoolTermList();
  }, []);

  return (
    <SmartCard title={t('panels.academic-calendar')}>
      {formOn && (
        <>
          <Row>
            {openScreen && (
              <FullScreenDialog
                title={contentForm.titleFullScreen || ''}
                toggleDialog={toggleFullScreen}
                isOpen={openScreen}
                toggleBackDialog={toggleFullScreen}
              >
                <FullScreenForm
                  onSubmit={addValuesInForm}
                  content={contentForm}
                  calendarMarkers={calendarMarkers}
                  selectedTerm={selectedTerm}
                  calendarCycles={calendarCycles}
                />
              </FullScreenDialog>
            )}
            <Col md={12} lg={4} xl={3}>
              <FormAcademicCalendar
                onSubmit={handleSubmit}
                backHandler={backClickHandler}
                dto={dto}
                weekdays={weekdays}
                calendarDays={calendarDays}
                onChangeCalendarDays={setCalendarDays}
                onChangeDto={onChangeDto}
                selectCalendar={selectCalendar}
                selectCycle={selectCycle}
                calendarMarkers={calendarMarkers}
                calendarCycles={calendarCycles}
                onChangeCalendarCycles={setCalendarCycles}
                listOfTerms={listOfTerms}
                selectedTerm={selectedTerm}
                onChangeSelectedTerm={loadCalendarBySchoolTermId}
                totalSchoolDay={totalSchoolDay}
              />
            </Col>
            <Col md={12} lg={8} xl={9}>
              <div className="form__form-group">
                <Weekdays
                  weekdays={weekdays}
                  dto={dto}
                  onChangeWorkingDaysInCalendar={onChangeWorkingDaysInCalendar}
                  dispatch={dispatch}
                />
                <YearCalendar
                  calendarDays={calendarDays}
                  onSelectEvent={selectEvent}
                  onRemoveEvent={removeEvent}
                />
              </div>
            </Col>
          </Row>
        </>
      )}
    </SmartCard>
  );
};

AcademicCalendarFormHandler.propTypes = {
  t: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
  match: ReactRouterPropTypes.match.isRequired,
};

export default withTranslation('common')(
  connect((state) => ({
    loading: state.loading,
  }))(AcademicCalendarFormHandler)
);
